import * as THREE from 'three';

/**
 * クッション反射を考慮した手球の予測軌跡（折れ線リボン）。
 * 自前モジュール（three のみ・**GPL非依存**）。距離が伸びるほど α が減衰する。
 * 点列（world x,y）を受け取り、各区間を半透明の帯として描く。
 */
export interface ReflectGuide {
  /** 点列で軌跡を更新（2点未満は非表示）。z はクロス上の高さ。 */
  update(points: ReadonlyArray<{ x: number; y: number }>, z: number): void;
  hide(): void;
  dispose(): void;
}

const WIDTH_R = 0.6; // 帯の幅 = ballR × これ
const FADE_DIST_R = 120; // α が 1/e に減衰する距離 = ballR × これ（緩やかに減衰）
const NEAR_FADE_R = 4; // 手玉付近のフェード長 = ballR × これ（重なりの違和感を避ける）
const BASE_ALPHA = 0.85;
const TINT = new THREE.Color(0xcfe8ff); // やや寒色の白

export const createReflectGuide = (
  scene: THREE.Scene,
  ballR: number,
  maxPoints: number,
): ReflectGuide => {
  const halfW = ballR * WIDTH_R * 0.5;
  const fadeDist = ballR * FADE_DIST_R;
  const nearFadeLen = ballR * NEAR_FADE_R;
  const maxVerts = maxPoints * 2; // 各点で左右2頂点
  const positions = new Float32Array(maxVerts * 3);
  const colors = new Float32Array(maxVerts * 4); // RGBA（α で減衰）

  // インデックス: 区間 i（点 i→i+1）ごとに 2 三角形（頂点 2i,2i+1,2i+2,2i+3）。
  const maxSegs = maxPoints - 1;
  const indices = new Uint16Array(maxSegs * 6);
  for (let i = 0; i < maxSegs; i++) {
    const v = i * 2;
    indices.set([v, v + 1, v + 2, v + 1, v + 3, v + 2], i * 6);
  }

  const geom = new THREE.BufferGeometry();
  const posAttr = new THREE.BufferAttribute(positions, 3);
  const colAttr = new THREE.BufferAttribute(colors, 4);
  posAttr.setUsage(THREE.DynamicDrawUsage);
  colAttr.setUsage(THREE.DynamicDrawUsage);
  geom.setAttribute('position', posAttr);
  geom.setAttribute('color', colAttr);
  geom.setIndex(new THREE.BufferAttribute(indices, 1));
  geom.setDrawRange(0, 0);

  const mat = new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    depthTest: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.renderOrder = 1;
  mesh.visible = false;
  scene.add(mesh);

  const update: ReflectGuide['update'] = (points, z) => {
    const n = Math.min(points.length, maxPoints);
    if (n < 2) {
      mesh.visible = false;
      return;
    }
    let dist = 0;
    for (let i = 0; i < n; i++) {
      const p = points[i]!;
      // 各点の進行方向（最後の点は前区間の向き）。
      const a = i < n - 1 ? points[i]! : points[i - 1]!;
      const b = i < n - 1 ? points[i + 1]! : points[i]!;
      let dirX = b.x - a.x;
      let dirY = b.y - a.y;
      const len = Math.hypot(dirX, dirY) || 1;
      dirX /= len;
      dirY /= len;
      const nx = -dirY * halfW; // 進行方向に直交（XY平面）
      const ny = dirX * halfW;

      if (i > 0) {
        const q = points[i - 1]!;
        dist += Math.hypot(p.x - q.x, p.y - q.y);
      }
      // 手玉付近は薄く（重なりの違和感回避）、遠方は距離で緩やかに減衰。
      const nearFade = Math.min(1, dist / nearFadeLen);
      const farFade = Math.exp(-dist / fadeDist);
      const alpha = BASE_ALPHA * nearFade * farFade;

      const o = i * 2;
      positions[o * 3] = p.x + nx;
      positions[o * 3 + 1] = p.y + ny;
      positions[o * 3 + 2] = z;
      positions[(o + 1) * 3] = p.x - nx;
      positions[(o + 1) * 3 + 1] = p.y - ny;
      positions[(o + 1) * 3 + 2] = z;
      for (const vi of [o, o + 1]) {
        colors[vi * 4] = TINT.r;
        colors[vi * 4 + 1] = TINT.g;
        colors[vi * 4 + 2] = TINT.b;
        colors[vi * 4 + 3] = alpha;
      }
    }
    posAttr.needsUpdate = true;
    colAttr.needsUpdate = true;
    geom.setDrawRange(0, (n - 1) * 6);
    mesh.visible = true;
  };

  return {
    update,
    hide() {
      mesh.visible = false;
    },
    dispose() {
      scene.remove(mesh);
      geom.dispose();
      mat.dispose();
    },
  };
};
